import { OpenInNew } from "@mui/icons-material";
import { Box, Link, Skeleton, Typography } from "@mui/material";
import { Carousel } from "../../../common/Carrousel";
import { useDetailsEndpoint } from "../../hooks/useDetailsEndpoint";
import { useInstallationComponentOrNull } from "../../hooks/useInstallationComponent";
import type { AppConfig } from "../../interfaces/appIntegration.interfaces";
import { StatusChip } from "../StatusChip";

interface Props {
  app: AppConfig;
  isInstalled: boolean;
  isFavorited?: boolean;
  statusLoading?: boolean;
  onInstall: () => void;
  onFavorite: () => void;
}

export const AppDetailHero = ({ app, isInstalled, statusLoading = false }: Props) => {
  const { data, refetch } = useDetailsEndpoint(app);
  const InstallationComponent = useInstallationComponentOrNull(app);


  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, width: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1.5fr)" },
            gap: 4,
            alignItems: "start",
            width: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "center", md: "flex-start" },
                gap: 2.5,
                mb: 4,
              }}
            >
              <Box
                component="img"
                src={app.icon}
                alt={app.name}
                sx={{
                  alignItems: { xs: "center", md: "flex-start" },
                  width: 80,
                  height: 80,
                  borderRadius: 2.5,
                  backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                  p: 1.5,
                  flexShrink: 0,
                }}
              />

              <Box sx={{ flex: 1, pt: 0.5, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: "1.8rem", sm: "1.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {app.name}
                  </Typography>

                  {statusLoading ? (
                    <Skeleton
                      variant="rounded"
                      width={72}
                      height={22}
                      animation="wave"
                      sx={{ borderRadius: 1 }}
                    />
                  ) : (
                    <StatusChip status={isInstalled ? "installed" : "not_installed"} />
                  )}
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    mb: 2,
                    lineHeight: 1.6,
                    maxWidth: 500,
                  }}
                >
                  {app.mediumDescription}
                </Typography>

                <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", display: "block", mb: 0.5 }}
                    >
                      Installs
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      1000+
                    </Typography>
                  </Box>
                </Box>

                <Link
                  href={app.documentationUrl}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "#2563eb",
                    textDecoration: "none",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    marginBottom: { xs: 4, md: 0 },
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Read Documentation <OpenInNew fontSize="small" />
                </Link>
              </Box>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            {app?.images?.length ? (
              <Box
                sx={{
                  width: 1,
                  borderRadius: 2.5,
                  overflow: "hidden",
                  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Carousel
                  images={app.images}
                  alt={app.name}
                  height={340}
                  navigation="both"
                  autoplay
                  autoplayInterval={10000}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: 380,
                  backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                }}
              >
                <Typography color="textSecondary">No images available</Typography>
              </Box>
            )}
          </Box>

          {InstallationComponent ? (
            <Box
              sx={{
                gridColumn: { xs: "1", md: "1 / -1" },
                width: 1,
                minWidth: 0,
              }}
            >
              <InstallationComponent refetch={refetch} details={data} app={app} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};